import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import PageHeader, { HeaderLink } from "../karkhana/ui/PageHeader";
import notifications from "../../common/Components/Notifications/Notification";
import { OrderCard, PartnerRow, listPartners, listProductionOrders, shortDate, stageLabel } from "../../services/partnerNetworkApi";
import { HealthDot, STATUS_LABEL } from "./pnUi";
import "../karkhana/ui/karkhana-ui.scss";
import "./partner-network.scss";

const COLUMNS: { id: string; label: string; match: (o: OrderCard) => boolean }[] = [
  { id: "NEGOTIATING", label: "Negotiating", match: (o) => o.status === "SENT" || o.status === "COUNTERED" },
  { id: "ACCEPTED", label: "Agreed", match: (o) => o.status === "ACCEPTED" && (!o.stage || o.stage === "ACCEPTED" || o.stage === "STARTED") },
  { id: "DYEING", label: "Dyeing", match: (o) => o.status === "ACCEPTED" && o.stage === "DYEING" },
  { id: "WEAVING", label: "Weaving", match: (o) => o.status === "ACCEPTED" && o.stage === "WEAVING" },
  { id: "FINISHING", label: "Finishing", match: (o) => o.status === "ACCEPTED" && o.stage === "FINISHING" },
  { id: "BLOCKED", label: "On hold / stuck", match: (o) => o.status === "ACCEPTED" && (o.stage === "ON_HOLD" || o.stage === "STUCK") },
  { id: "COMPLETED", label: "Completed", match: (o) => o.status === "ACCEPTED" && o.stage === "COMPLETED" },
];

export const OrderCardView = ({ o, to, showPartner = true }: { o: OrderCard; to: string; showPartner?: boolean }) => {
  const done = o.piecesDone ?? 0;
  const total = o.quantity ?? 0;
  return (
    <Link className="pn-ocard" to={to}>
      <div className="pn-ocard__top">
        <span>{o.code}</span>
        <HealthDot health={o.health} />
      </div>
      <div className="pn-ocard__title">{o.title}</div>
      <div className="pn-ocard__partner">{showPartner ? o.partnerName : o.designer}</div>
      {o.status === "ACCEPTED" ? (
        <div className="pn-ocard__progress" aria-hidden="true">
          <span style={{ width: `${total ? Math.min(100, (done / total) * 100) : 0}%` }} />
        </div>
      ) : (
        <div style={{ margin: "8px 0 6px" }}>
          <span className={`pn-chip${o.status === "COUNTERED" ? " is-warn" : ""}`}>{STATUS_LABEL[o.status] || o.status}</span>
        </div>
      )}
      <div className="pn-ocard__nums">
        <span>{o.status === "ACCEPTED" ? `${done} / ${total || "—"} pcs` : `${total || "—"} pcs`}</span>
        <span>Due {shortDate(o.dueDate)}</span>
      </div>
      {o.lastUpdate ? (
        <div className="pn-ocard__last">
          {o.lastUpdate.kind === "STAGE"
            ? stageLabel(o.lastUpdate.stage)
            : o.lastUpdate.kind === "PROGRESS"
            ? `${o.lastUpdate.piecesDone} pieces done`
            : o.lastUpdate.text || o.lastUpdate.kind.toLowerCase()}{" "}
          · {shortDate(o.lastUpdate.at)}
        </div>
      ) : null}
    </Link>
  );
};

const ProductionBoard = () => {
  const [orders, setOrders] = useState<OrderCard[]>([]);
  const [partners, setPartners] = useState<PartnerRow[]>([]);
  const [partner, setPartner] = useState("");
  const [group, setGroup] = useState("");
  const [overdue, setOverdue] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    listPartners().then((res) => res.success && setPartners(res.data.partners));
  }, []);

  useEffect(() => {
    setLoading(true);
    listProductionOrders({ partnershipId: partner || undefined, group: group || undefined, overdue }).then((res) => {
      setLoading(false);
      if (res.success) setOrders(res.data);
      else notifications({ title: "Production", message: res.message, type: "danger" });
    });
  }, [partner, group, overdue]);

  const groups = useMemo(() => Array.from(new Set(partners.flatMap((p) => p.groupIds || []))), [partners]);

  return (
    <div className="pn-page">
      <PageHeader title="Production">
        <HeaderLink to="/partner-network/partners">Partners</HeaderLink>
        <HeaderLink to="/partner-network/orders/new" primary add>
          New order
        </HeaderLink>
      </PageHeader>
      <div className="pn-filters">
        <select aria-label="Partner" value={partner} onChange={(e) => setPartner(e.target.value)}>
          <option value="">All partners</option>
          {partners.map((p) => (
            <option key={p.id} value={p.id}>{p.name}</option>
          ))}
        </select>
        {groups.length ? (
          <select aria-label="Group" value={group} onChange={(e) => setGroup(e.target.value)}>
            <option value="">All groups</option>
            {groups.map((g) => <option key={g} value={g}>{g}</option>)}
          </select>
        ) : null}
        <label>
          <input type="checkbox" checked={overdue} onChange={(e) => setOverdue(e.target.checked)} /> Overdue updates
        </label>
      </div>
      {!loading && !orders.length ? (
        <div className="pn-card"><p className="pn-muted">No production orders here yet.</p></div>
      ) : null}
      <div className="pn-board">
        {COLUMNS.map((col) => {
          const items = orders.filter(col.match);
          if (!items.length && (col.id === "BLOCKED" || col.id === "COMPLETED")) return null;
          return (
            <section key={col.id} className={`pn-col${items.length ? "" : " is-empty"}`} aria-label={col.label}>
              <h3>
                <span>{col.label}</span>
                <span>{items.length}</span>
              </h3>
              {items.map((o) => (
                <OrderCardView key={o.id} o={o} to={`/partner-network/orders/${o.id}`} />
              ))}
            </section>
          );
        })}
      </div>
    </div>
  );
};

export default ProductionBoard;
